import React, { useState, useEffect, useCallback, useRef } from "react";
import { assistantV2Service } from "../services/assistantV2Service";
import AssistantLayout from "../components/assistant-v2/AssistantLayout";
import MessageRenderer from "../components/assistant-v2/MessageRenderer";

const AssistantRHV2Page = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const suggestedQuestions = [
    "Combien d'employés sont enregistrés ?",
    "Qui est le manager de Mariem Khalfallah ?",
    "Quels congés sont en attente ?",
    "Quel est mon solde de congé ?",
    "Quelle offre est située à Ariana ?",
    "Quels types de contrat sont disponibles ?",
    "Quel est le meilleur candidat pour Data Scientist IA ?",
    "Affiche le Top 3 des candidats.",
    "Quels entretiens sont prévus cette semaine ?",
    "Comment planifier un entretien ?",
  ];

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await assistantV2Service.getHistory();
      setConversations(data.conversations || []);
    } catch (err) {
      console.error("Error loading history:", err);
      setError(err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const loadConversation = useCallback(async (conversationId) => {
    setLoading(true);
    try {
      const data = await assistantV2Service.getConversation(conversationId);
      setCurrentConversationId(conversationId);
      setMessages(
        data.messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at,
        }))
      );
      setError(null);
    } catch (err) {
      console.error("Error loading conversation:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setError(null);
  };

  const handleSelectConversation = (conversationId) => {
    loadConversation(conversationId);
  };

  const handleDeleteConversation = (conversationId) => {
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    if (currentConversationId === conversationId) {
      handleNewChat();
    }
  };

  const handleDeleteAll = () => {
    setConversations([]);
    handleNewChat();
  };

  const handleRenameConversation = (conversationId, newTitle) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId ? { ...c, title: newTitle } : c
      )
    );
  };

  const handleClearConversation = () => {
    setMessages([]);
    setError(null);
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  };

  const handleFeedback = (messageId, isPositive) => {
    console.log(`Feedback for message ${messageId}: ${isPositive ? 'positive' : 'negative'}`);
    // TODO: Send feedback to backend
  };

  const handleToggleSources = (show) => {
    console.log(`Toggle sources: ${show}`);
    // Sources are handled internally in ChatWindow
  };

  const handleSendMessage = useCallback(async (question) => {
    if (loading) return;

    // Annuler la requête précédente si elle existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    // Ajouter le message utilisateur
    const userMessage = {
      role: "user",
      content: question,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await assistantV2Service.chat(
        question,
        currentConversationId,
        abortControllerRef.current.signal
      );

      // Mettre à jour l'ID de conversation
      if (response.conversation_id && !currentConversationId) {
        setCurrentConversationId(response.conversation_id);
        // Recharger l'historique pour inclure la nouvelle conversation
        loadHistory();
      }

      // Ajouter la réponse de l'assistant
      const assistantMessage = {
        role: "assistant",
        content: response.answer,
        displayType: response.display_type,
        data: response.data,
        sources: response.sources,
        intent: response.intent,
        responseTime: response.response_time_ms,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sending message:", err);
        setError(err);
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [loading, currentConversationId, loadHistory]);

  const handleRetry = () => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages]
        .reverse()
        .find((msg) => msg.role === "user");
      if (lastUserMessage) {
        handleSendMessage(lastUserMessage.content);
      }
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
  };

  const handleRegenerate = (message) => {
    if (message.role === "assistant") {
      const lastUserMessage = messages[messages.indexOf(message) - 1];
      if (lastUserMessage && lastUserMessage.role === "user") {
        setMessages((prev) => prev.slice(0, messages.indexOf(message)));
        handleSendMessage(lastUserMessage.content);
      }
    }
  };

  const handleSelectSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <AssistantLayout
      conversations={conversations}
      currentConversationId={currentConversationId}
      messages={messages.map((msg) => ({
        ...msg,
        content:
          msg.role === "assistant" && msg.displayType ? (
            <MessageRenderer
              answer={msg.content}
              displayType={msg.displayType}
              data={msg.data}
              title={msg.title}
            />
          ) : (
            msg.content
          ),
      }))}
      loading={loading}
      error={error}
      onNewChat={handleNewChat}
      onSelectConversation={handleSelectConversation}
      onDeleteConversation={handleDeleteConversation}
      onDeleteAll={handleDeleteAll}
      onRenameConversation={handleRenameConversation}
      onSendMessage={handleSendMessage}
      onRetry={handleRetry}
      onCopy={handleCopy}
      onRegenerate={handleRegenerate}
      onFeedback={handleFeedback}
      onToggleSources={handleToggleSources}
      onStopGeneration={handleStopGeneration}
      onClearConversation={handleClearConversation}
      suggestedQuestions={suggestedQuestions}
      onSelectSuggestedQuestion={handleSelectSuggestedQuestion}
      loadingHistory={loadingHistory}
    />
  );
};

export default AssistantRHV2Page;
